import React, { useState, useEffect } from 'react';
import Draggable from 'react-draggable';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { textdb } from '../../../firebaseConfig';
import { useParams, useNavigate } from 'react-router-dom';
import { FaSave, FaImage, FaFont } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import styles from './Edit.module.css'; // Import the new CSS Module

const fontFamilies = [
  'Roboto', 'Montserrat', 'Poppins', 'Open Sans', 'Lato', 'Arial', 'Georgia', 'Verdana', 'Times New Roman', 'Courier New', 'Impact'
];

export const Edit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // All Editable Properties
  const [galleryData, setGalleryData] = useState(null);
  const [fontFamily, setFontFamily] = useState('Roboto');
  const [fontColor, setFontColor] = useState('#000000');
  const [fontWeight, setFontWeight] = useState('normal');
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [textPosition, setTextPosition] = useState({ x: 0, y: 0 });
  const [imageSize, setImageSize] = useState({ width: 100, height: 100 });
  const [textSize, setTextSize] = useState(24);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const galleryDocRef = doc(textdb, 'galleries', id);
        const positionsDocRef = doc(textdb, 'positions', id);

        const [gallerySnap, positionsSnap] = await Promise.all([
          getDoc(galleryDocRef),
          getDoc(positionsDocRef)
        ]);
        
        if (gallerySnap.exists()) {
          const data = gallerySnap.data();
          setGalleryData(data);
          setFontFamily(data.fontFamily || 'Roboto');
          setFontColor(data.fontColor || '#000000');
          setFontWeight(data.fontWeight || 'normal');
        } else {
          toast.error("Campaign data not found!");
        }

        if (positionsSnap.exists()) {
          const data = positionsSnap.data();
          setImagePosition(data.imagePosition || { x: 50, y: 150 });
          setTextPosition(data.textPosition || { x: 50, y: 50 });
          setImageSize(data.imageSize || { width: 100, height: 100 });
          setTextSize(data.textSize || 24);
        } else {
          toast.error("Layout data not found!");
        }
      } catch (error) {
        toast.error("Failed to load campaign data.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const handleSaveChanges = async () => {
    setIsSaving(true);
    toast.info("Saving changes...", { toastId: 'saving' });
    try {
      const galleryDocRef = doc(textdb, 'galleries', id);
      const positionsDocRef = doc(textdb, 'positions', id);

      await setDoc(galleryDocRef, {
        fontFamily,
        fontColor,
        fontWeight,
      }, { merge: true });

      await setDoc(positionsDocRef, {
        imagePosition,
        textPosition,
        imageSize,
        textSize,
      });

      toast.update('saving', { render: "All changes saved! Redirecting...", type: "success", autoClose: 2000 });
      
      setTimeout(() => {
        navigate('/admin'); // Or your main admin route
      }, 2000);

    } catch (error) {
      toast.update('saving', { render: "Failed to save changes.", type: "error", autoClose: 3000 });
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <div className={styles.loadingWrapper}><h2>Loading Editor...</h2></div>;
  }

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.container}>
        <ToastContainer position="bottom-right" theme="colored" />
        <div className={styles.editorLayout}>
          
          {/* --- Preview Panel (Left on Desktop) --- */}
          <div className={styles.previewPanel}>
             <div className={styles.editorPreviewWrapper}>
              <div className={styles.mainImagePreview} style={{ height: `${galleryData?.imageHeight}px`, width: `${galleryData?.imageWidth}px` }}>
                <img className={styles.bgImage} src={galleryData?.imageUrl} alt="Background" />
                <div className={styles.dragContainer}>
                  <Draggable position={imagePosition} onStop={(e, d) => setImagePosition({ x: d.x, y: d.y })}>
                    <div className={`${styles.draggableItem} ${styles.placeholderImage}`} style={{ width: `${imageSize.width}px`, height: `${imageSize.height}px`, borderRadius: galleryData?.borderRadius }}>
                      <FaImage /><span>User Image</span>
                    </div>
                  </Draggable>
                  <Draggable position={textPosition} onStop={(e, d) => setTextPosition({ x: d.x, y: d.y })}>
                    <div className={`${styles.draggableItem} ${styles.placeholderText}`} style={{ fontFamily, color: fontColor, fontSize: `${textSize}px`, fontWeight }}>
                      User Name
                    </div>
                  </Draggable>
                </div>
              </div>
            </div>
          </div>

          {/* --- Controls Panel (Right on Desktop) --- */}
          <div className={styles.controlsPanel}>
            <div className={styles.controlsCard}>
              <div className={styles.controlsHeader}>
                <h2>Edit Layout & Style</h2>
              </div>
              <div className={styles.controlsContent}>
                <div className={styles.formGroup}>
                  <label>User Image Size</label>
                  <input className={styles.rangeSlider} type="range" min="40" max="250" value={imageSize.width} onChange={e => setImageSize({ width: +e.target.value, height: +e.target.value })} />
                </div>
                <div className={styles.formGroup}>
                  <label>User Name Size</label>
                  <input className={styles.rangeSlider} type="range" min="12" max="60" value={textSize} onChange={e => setTextSize(+e.target.value)} />
                </div>

                <hr className={styles.divider}/>

                <div className={styles.formGroup}>
                  <label>Font Family</label>
                  <select value={fontFamily} onChange={e => setFontFamily(e.target.value)}>
                      {fontFamilies.map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label>Font Weight</label>
                  <div className={styles.btnGroup}>
                    <button className={`${styles.btn} ${fontWeight === 'normal' ? styles.primary : styles.secondary}`} onClick={() => setFontWeight('normal')}>Normal</button>
                    <button className={`${styles.btn} ${fontWeight === 'bold' ? styles.primary : styles.secondary}`} onClick={() => setFontWeight('bold')}>Bold</button>
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label>Font Color</label>
                  <div className={styles.colorInputWrapper}>
                    <input type="color" value={fontColor} onChange={e => setFontColor(e.target.value)} />
                  </div>
                </div>
              </div>
              <div className={styles.controlsFooter}>
                <button className={`${styles.btn} ${styles.success}`} onClick={handleSaveChanges} disabled={isSaving}>
                  <FaSave /> {isSaving ? "Saving..." : "Save All Changes"}
                </button>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default Edit;